import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useId,
} from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import { fetchAdminCampaigns } from '@/lib/admin'
import {
  fetchGlobalSplits,
  fetchCampaignSplits,
  createCampaignSplit,
  updateCampaignSplit,
  deleteCampaignSplit,
} from '@/lib/splits'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import { isCampaignTotpStepUpError } from '@/lib/campaign-totp-step-up'
import type { AdminCampaignListItem, Split } from '@/types/api'
import styles from './SplitsGlobaisPage.module.css'
import { SplitFormRows } from './SplitFormRows'
import {
  createEmptySplitFormItem,
  formatSplitValueLabel,
  isPersistedSplitId,
  mapApiSplitToFormItem,
  mapApiSplitToNewFormItem,
  toSplitCreatePayload,
  toSplitUpdatePayload,
  validateSplitFormItems,
  type SplitFormItem,
} from './split-form'

export function SplitsPersonalizadoTab() {
  const { runWithStepUp } = useCampaignTotpStepUp()
  const [campaigns, setCampaigns] = useState<AdminCampaignListItem[]>([])
  const [campaignsLoading, setCampaignsLoading] = useState(true)
  const [campaignsError, setCampaignsError] = useState<string | null>(null)
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null)
  const [splits, setSplits] = useState<SplitFormItem[]>(() => [createEmptySplitFormItem()])
  const [lastFetchedIds, setLastFetchedIds] = useState<number[]>([])
  const [splitsLoading, setSplitsLoading] = useState(false)
  const [splitsLoadError, setSplitsLoadError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)
  const [splitToDelete, setSplitToDelete] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [campaignDropdownOpen, setCampaignDropdownOpen] = useState(false)
  const [campaignSearch, setCampaignSearch] = useState('')
  const [highlightedCampaignId, setHighlightedCampaignId] = useState<string | null>(null)
  const campaignContainerRef = useRef<HTMLDivElement>(null)
  const campaignListRef = useRef<HTMLDivElement>(null)
  const campaignTriggerRef = useRef<HTMLButtonElement>(null)
  const [campaignListPosition, setCampaignListPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  })
  const [globalSplits, setGlobalSplits] = useState<Split[]>([])
  const [globalSplitsLoading, setGlobalSplitsLoading] = useState(false)
  const [globalSplitsError, setGlobalSplitsError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setCampaignsLoading(true)
    setCampaignsError(null)
    fetchAdminCampaigns({ perPage: 100 })
      .then((data) => {
        if (!cancelled) setCampaigns(data.campaigns)
      })
      .catch(() => {
        if (!cancelled) setCampaignsError('Não foi possível carregar as campanhas.')
      })
      .finally(() => {
        if (!cancelled) setCampaignsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const filteredCampaigns = useMemo(() => {
    const q = campaignSearch.trim().toLowerCase()
    if (!q) return campaigns
    return campaigns.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.institution_name?.toLowerCase().includes(q) ?? false)
    )
  }, [campaigns, campaignSearch])

  const selectedCampaign = useMemo(
    () => campaigns.find((c) => String(c.id) === selectedCampaignId),
    [campaigns, selectedCampaignId]
  )

  const campaignListId = useId()
  const campaignDropdownId = `campaign-dropdown-${campaignListId}`

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        campaignContainerRef.current &&
        !campaignContainerRef.current.contains(event.target as Node) &&
        campaignListRef.current &&
        !campaignListRef.current.contains(event.target as Node)
      ) {
        setCampaignDropdownOpen(false)
        setCampaignSearch('')
        setHighlightedCampaignId(null)
      }
    }
    if (campaignDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [campaignDropdownOpen])

  useEffect(() => {
    if (campaignDropdownOpen && campaignTriggerRef.current) {
      const rect = campaignTriggerRef.current.getBoundingClientRect()
      setCampaignListPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 320),
      })
    }
  }, [campaignDropdownOpen])

  const handleCampaignToggle = useCallback(() => {
    setCampaignDropdownOpen((prev) => !prev)
    if (!campaignDropdownOpen) {
      setCampaignSearch('')
      setHighlightedCampaignId(selectedCampaignId ?? null)
    }
  }, [campaignDropdownOpen, selectedCampaignId])

  const handleCampaignSelect = useCallback((value: string) => {
    setSelectedCampaignId(value.trim() || null)
    setSaveMessage(null)
    setCampaignDropdownOpen(false)
    setCampaignSearch('')
    setHighlightedCampaignId(null)
    campaignTriggerRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!selectedCampaignId) {
      setSplits([createEmptySplitFormItem()])
      setLastFetchedIds([])
      setSplitsLoadError(null)
      setSplitsLoading(false)
      return
    }
    const campaignId = Number(selectedCampaignId)
    if (!Number.isFinite(campaignId)) return

    let cancelled = false
    setSplitsLoading(true)
    setSplitsLoadError(null)
    void runWithStepUp(() => fetchCampaignSplits(campaignId))
      .then((data) => {
        if (cancelled) return
        const list = data.splits
        if (list.length === 0) {
          setSplits([createEmptySplitFormItem()])
          setLastFetchedIds([])
        } else {
          setSplits(list.map(mapApiSplitToFormItem))
          setLastFetchedIds(list.map((s) => s.id))
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setSplitsLoadError(err instanceof Error ? err.message : 'Erro ao carregar splits.')
          setSplits([createEmptySplitFormItem()])
          setLastFetchedIds([])
        }
      })
      .finally(() => {
        if (!cancelled) setSplitsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [runWithStepUp, selectedCampaignId])

  useEffect(() => {
    if (!selectedCampaignId) {
      setGlobalSplits([])
      setGlobalSplitsError(null)
      setGlobalSplitsLoading(false)
      return
    }
    let cancelled = false
    setGlobalSplitsLoading(true)
    setGlobalSplitsError(null)
    void runWithStepUp(() => fetchGlobalSplits())
      .then((data) => {
        if (!cancelled) setGlobalSplits(data.splits)
      })
      .catch((err) => {
        if (!cancelled) {
          setGlobalSplitsError(
            err instanceof Error ? err.message : 'Erro ao carregar splits padrão.'
          )
          setGlobalSplits([])
        }
      })
      .finally(() => {
        if (!cancelled) setGlobalSplitsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [runWithStepUp, selectedCampaignId])

  const handleAddGlobalSplit = useCallback((split: Split) => {
    setSplits((prev) => [...prev, mapApiSplitToNewFormItem(split)])
  }, [])

  const handleUseAllGlobalSplits = useCallback(() => {
    if (globalSplits.length === 0) return
    setSplits((prev) => [...prev, ...globalSplits.map(mapApiSplitToNewFormItem)])
  }, [globalSplits])

  const addSplit = useCallback(() => {
    setSplits((prev) => [...prev, createEmptySplitFormItem()])
  }, [])

  const openDeleteModal = useCallback((id: string) => {
    setSplitToDelete(id)
  }, [])

  const closeDeleteModal = useCallback(() => {
    if (!isDeleting) setSplitToDelete(null)
  }, [isDeleting])

  const confirmDeleteSplit = useCallback(async () => {
    if (!splitToDelete || !selectedCampaignId) return
    const campaignId = Number(selectedCampaignId)
    if (!Number.isFinite(campaignId)) return

    const idToRemove = splitToDelete
    const wasBackend = isPersistedSplitId(idToRemove)
    const previousSplits = splits
    const previousIds = lastFetchedIds

    setSplitToDelete(null)

    setSplits((prev) => {
      const next = prev.filter((s) => s.id !== idToRemove)
      return next.length > 0 ? next : [createEmptySplitFormItem()]
    })
    if (wasBackend) {
      setLastFetchedIds((prev) => prev.filter((id) => id !== Number(idToRemove)))
    }

    if (!wasBackend) return

    setIsDeleting(true)
    setSaveMessage(null)
    try {
      await runWithStepUp(() => deleteCampaignSplit(campaignId, Number(idToRemove)))
      const fresh = await runWithStepUp(() => fetchCampaignSplits(campaignId))
      const list = fresh.splits
      if (list.length === 0) {
        setSplits([createEmptySplitFormItem()])
        setLastFetchedIds([])
      } else {
        setSplits(list.map(mapApiSplitToFormItem))
        setLastFetchedIds(list.map((s) => s.id))
      }
      setSaveMessage('Split excluído com sucesso.')
    } catch (err) {
      if (isCampaignTotpStepUpError(err)) {
        setSplits(previousSplits.length > 0 ? previousSplits : [createEmptySplitFormItem()])
        setLastFetchedIds(previousIds)
        return
      }

      try {
        const fresh = await fetchCampaignSplits(campaignId)
        const list = fresh.splits
        if (list.length === 0) {
          setSplits([createEmptySplitFormItem()])
          setLastFetchedIds([])
        } else {
          setSplits(list.map(mapApiSplitToFormItem))
          setLastFetchedIds(list.map((s) => s.id))
        }
      } catch {
        setSplits(previousSplits.length > 0 ? previousSplits : [createEmptySplitFormItem()])
        setLastFetchedIds(previousIds)
      }
      setSaveMessage(
        err instanceof Error ? err.message : 'Não foi possível excluir o split.'
      )
    } finally {
      setIsDeleting(false)
    }
  }, [splitToDelete, selectedCampaignId, runWithStepUp, splits, lastFetchedIds])

  const updateSplit = useCallback((id: string, patch: Partial<Omit<SplitFormItem, 'id'>>) => {
    setSplits((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)))
  }, [])

  const handleSave = useCallback(async () => {
    if (!selectedCampaignId) return
    const campaignId = Number(selectedCampaignId)
    if (!Number.isFinite(campaignId)) return

    setSaveMessage(null)
    const currentBackendIds = splits.filter((s) => isPersistedSplitId(s.id)).map((s) => Number(s.id))
    const toDelete = lastFetchedIds.filter((id) => !currentBackendIds.includes(id))
    const validationError = validateSplitFormItems(splits)
    if (validationError) {
      setSaveMessage(validationError)
      return
    }

    setIsSaving(true)
    try {
      for (const id of toDelete) {
        await runWithStepUp(() => deleteCampaignSplit(campaignId, id))
      }
      for (const s of splits) {
        if (!isPersistedSplitId(s.id)) continue
        await runWithStepUp(() =>
          updateCampaignSplit(campaignId, Number(s.id), toSplitUpdatePayload(s))
        )
      }
      for (const s of splits) {
        if (isPersistedSplitId(s.id)) continue
        const payload = toSplitCreatePayload(s)
        if (!payload) continue
        await runWithStepUp(() => createCampaignSplit(campaignId, payload))
      }

      const fresh = await runWithStepUp(() => fetchCampaignSplits(campaignId))
      const list = fresh.splits
      if (list.length === 0) {
        setSplits([createEmptySplitFormItem()])
        setLastFetchedIds([])
      } else {
        setSplits(list.map(mapApiSplitToFormItem))
        setLastFetchedIds(list.map((s) => s.id))
      }
      setSaveMessage('Splits personalizados salvos com sucesso.')
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Não foi possível salvar. Tente novamente.'
      setSaveMessage(msg)
    } finally {
      setIsSaving(false)
    }
  }, [selectedCampaignId, splits, lastFetchedIds, runWithStepUp])

  const hasCampaignSelected = Boolean(selectedCampaignId)

  return (
    <div className={styles.tabContent}>
      <div className={styles.campaignCard}>
        <h2 className={styles.splitsSectionTitle}>Campanha</h2>
        <p className={styles.campaignCardDescription}>
          Selecione a campanha para personalizar o split.
        </p>
        {campaignsLoading ? (
          <p className={styles.campaignCardLoading}>Carregando campanhas...</p>
        ) : campaignsError ? (
          <p id="splits-campaigns-error" className={styles.campaignCardError} role="alert">
            {campaignsError}
          </p>
        ) : (
          <div
            ref={campaignContainerRef}
            className={styles.campaignTriggerWrap}
          >
            <button
              ref={campaignTriggerRef}
              id={campaignDropdownId}
              type="button"
              role="combobox"
              aria-expanded={campaignDropdownOpen}
              aria-haspopup="listbox"
              aria-controls={`${campaignDropdownId}-list`}
              aria-label="Selecionar campanha"
              onClick={handleCampaignToggle}
              className={[
                styles.campaignTrigger,
                campaignDropdownOpen && styles.campaignTriggerOpen,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span className={styles.campaignTriggerIconWrap}>
                <Icon
                  icon="mdi:bullhorn-outline"
                  className={styles.campaignTriggerIcon}
                />
              </span>
              <span className={styles.campaignTriggerText}>
                <span className={styles.campaignTriggerLabel}>
                  Campanha
                </span>
                <span
                  className={[
                    styles.campaignTriggerValue,
                    !selectedCampaign && styles.campaignTriggerPlaceholder,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {selectedCampaign
                    ? selectedCampaign.name
                    : 'Selecione uma campanha'}
                </span>
                {selectedCampaign?.institution_name && (
                  <span className={styles.campaignTriggerSubtitle}>
                    {selectedCampaign.institution_name}
                  </span>
                )}
              </span>
              <Icon
                icon="mdi:chevron-down"
                className={[
                  styles.campaignChevron,
                  campaignDropdownOpen && styles.campaignChevronOpen,
                ]
                  .filter(Boolean)
                  .join(' ')}
              />
            </button>
            {campaignDropdownOpen &&
              typeof document !== 'undefined' &&
              createPortal(
                <div
                  ref={campaignListRef}
                  id={`${campaignDropdownId}-list`}
                  role="listbox"
                  aria-label="Campanhas"
                  className={styles.campaignDropdown}
                  style={{
                    top: `${campaignListPosition.top}px`,
                    left: `${campaignListPosition.left}px`,
                    width: `${campaignListPosition.width}px`,
                  }}
                >
                  <div className={styles.campaignDropdownSearchWrap}>
                    <Icon
                      icon="mdi:magnify"
                      className={styles.campaignDropdownSearchIcon}
                    />
                    <input
                      type="text"
                      className={styles.campaignDropdownSearchInput}
                      placeholder="Buscar campanha..."
                      value={campaignSearch}
                      onChange={(e) => setCampaignSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setCampaignSearch('')
                          campaignTriggerRef.current?.focus()
                        }
                      }}
                      autoFocus
                      aria-label="Buscar campanha"
                    />
                  </div>
                  <div className={styles.campaignDropdownList}>
                    {filteredCampaigns.length === 0 ? (
                      <div className={styles.campaignDropdownEmpty}>
                        Nenhuma campanha encontrada
                      </div>
                    ) : (
                      filteredCampaigns.map((c) => {
                        const value = String(c.id)
                        const isSelected = value === selectedCampaignId
                        const isHighlighted = value === highlightedCampaignId
                        return (
                          <button
                            key={c.id}
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            className={[
                              styles.campaignDropdownOption,
                              isSelected && styles.campaignDropdownOptionSelected,
                              isHighlighted &&
                                styles.campaignDropdownOptionHighlighted,
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onClick={() => handleCampaignSelect(value)}
                            onMouseEnter={() => setHighlightedCampaignId(value)}
                          >
                            <span className={styles.campaignDropdownOptionIcon}>
                              <Icon icon="mdi:bullhorn-outline" />
                            </span>
                            <span className={styles.campaignDropdownOptionText}>
                              <span className={styles.campaignDropdownOptionLabel}>
                                {c.name}
                              </span>
                              <span className={styles.campaignDropdownOptionSubtitle}>
                                {c.institution_name ?? 'Sem instituição'}
                              </span>
                            </span>
                            {isSelected && (
                              <Icon
                                icon="mdi:check"
                                className={styles.campaignDropdownOptionCheck}
                              />
                            )}
                          </button>
                        )
                      })
                    )}
                  </div>
                </div>,
                document.body
              )}
          </div>
        )}
      </div>

      {hasCampaignSelected && (
        <div className={styles.globalSplitsSection}>
          <h2 className={styles.globalSplitsTitle}>Splits padrão</h2>
          <p className={styles.globalSplitsDescription}>
            Clique para adicionar aos splits personalizados.
          </p>
          {globalSplitsLoading ? (
            <p className={styles.campaignCardLoading} role="status">
              Carregando splits padrão...
            </p>
          ) : globalSplitsError ? (
            <p className={styles.campaignCardError} role="alert">
              {globalSplitsError}
            </p>
          ) : globalSplits.length === 0 ? (
            <p className={styles.globalSplitsEmpty}>
              Nenhum split padrão configurado.
            </p>
          ) : (
            <>
              <div className={styles.globalSplitsList}>
                {globalSplits.map((split) => (
                  <div key={split.id} className={styles.globalSplitCard}>
                    <div className={styles.globalSplitCardInfo}>
                      <span className={styles.globalSplitCardNome}>{split.name}</span>
                      <span className={styles.globalSplitCardMeta}>
                        ID Doey: {split.doey_user_id || '—'} · {formatSplitValueLabel(split)}
                      </span>
                    </div>
                    <button
                      type="button"
                      className={styles.globalSplitCardAdd}
                      onClick={() => handleAddGlobalSplit(split)}
                      aria-label={`Adicionar split ${split.name}`}
                    >
                      <Icon icon="mdi:plus" width={18} height={18} />
                      Adicionar
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className={styles.globalSplitsUseAllBtn}
                onClick={handleUseAllGlobalSplits}
                disabled={globalSplits.length === 0}
                aria-label="Usar todos os splits padrão"
              >
                <Icon icon="mdi:content-copy" width={18} height={18} />
                Usar todos os splits padrão
              </button>
            </>
          )}
        </div>
      )}

      <div className={styles.splitsSection}>
        <div className={styles.splitsHeader}>
          <h2 className={styles.splitsSectionTitle}>Splits personalizados</h2>
          {hasCampaignSelected && (
            <button
              type="button"
              className={styles.addSplitBtn}
              onClick={addSplit}
              aria-label="Adicionar split"
            >
              <Icon icon="mdi:plus" width={20} height={20} />
              Adicionar split
            </button>
          )}
        </div>

        {!hasCampaignSelected ? (
          <p className={styles.campaignCardDescription}>
            Selecione uma campanha acima para configurar os splits personalizados.
          </p>
        ) : splitsLoading ? (
          <p className={styles.campaignCardDescription} role="status">
            Carregando splits...
          </p>
        ) : splitsLoadError ? (
          <p className={styles.campaignCardError} role="alert">
            {splitsLoadError}
          </p>
        ) : (
          <>
          <SplitFormRows
            splits={splits}
            idPrefix="split-pers"
            onChange={updateSplit}
            onRemove={openDeleteModal}
          />

          <div className={styles.splitsFooter}>
            {saveMessage && (
              <p className={styles.saveMessage} role="status">
                {saveMessage}
              </p>
            )}
            <button
              type="button"
              className={styles.saveBtn}
              onClick={handleSave}
              disabled={isSaving}
              aria-busy={isSaving}
            >
              {isSaving ? (
                <>
                  <Icon
                    icon="mdi:loading"
                    className={styles.spinnerIcon}
                    width={20}
                    height={20}
                  />
                  Salvando...
                </>
              ) : (
                <>
                  <Icon
                    icon="mdi:content-save-outline"
                    width={20}
                    height={20}
                  />
                  Salvar
                </>
              )}
            </button>
          </div>
          </>
        )}
      </div>

      {splitToDelete !== null && (
        <div
          className={styles.confirmModalOverlay}
          onClick={closeDeleteModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-delete-split-title"
        >
          <div
            className={styles.confirmModal}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.confirmModalHeader}>
              <h3 id="confirm-delete-split-title" className={styles.confirmModalTitle}>
                Excluir split
              </h3>
            </div>
            <div className={styles.confirmModalBody}>
              Deseja realmente excluir este split? Esta ação não pode ser desfeita.
            </div>
            <div className={styles.confirmModalFooter}>
              <button
                type="button"
                className={`${styles.confirmModalBtn} ${styles.confirmModalBtnCancel}`}
                onClick={closeDeleteModal}
                disabled={isDeleting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={`${styles.confirmModalBtn} ${styles.confirmModalBtnConfirm}`}
                onClick={confirmDeleteSplit}
                disabled={isDeleting}
                aria-busy={isDeleting}
              >
                {isDeleting ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
